import type { EInvoiceDocType } from '@shared/dto/e-invoice/e-invoice-doc-type.enum';
import type { EInvoiceStatus } from '@shared/dto/e-invoice/e-invoice-status.enum';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

export interface EInvoiceListItem {
  id: string;
  tenantId: string;
  tenantName: string;
  ownerEmail: string | null;
  stripeInvoiceId: string;
  docType: EInvoiceDocType;
  numero: string | null;
  status: EInvoiceStatus;
  progressivoSeq: number | null;
  fileName: string | null;
  validationErrors: string[] | null;
  totalCents: number;
  currency: string;
  invoicedAt: string;
  generatedAt: string | null;
  voidedAt: string | null;
}

export interface EInvoiceListParams {
  status?: EInvoiceStatus;
  docType?: EInvoiceDocType;
  search?: string;
  tenantId?: string;
  from?: string;
  to?: string;
  skip?: number;
  take?: number;
  orderBy?: string;
  orderDir?: 'asc' | 'desc';
}

export interface EInvoiceListResponse {
  items: EInvoiceListItem[];
  total: number;
  facets: { status: EInvoiceStatus; count: number }[];
}

export interface GenerateBatchResult {
  results: { stripeInvoiceId: string; status: string; errors?: string[] }[];
  summary: { total: number; generated: number; invalid: number; failed: number };
}

export interface IEInvoicesService {
  list(params?: EInvoiceListParams): Promise<EInvoiceListResponse>;
  getOne(id: string): Promise<EInvoiceListItem>;
  generate(stripeInvoiceId: string, force?: boolean): Promise<EInvoiceListItem>;
  generateBatch(stripeInvoiceIds: string[]): Promise<GenerateBatchResult>;
  exportZip(stripeInvoiceIds: string[]): Promise<Blob>;
  downloadXml(id: string): Promise<Blob>;
}

@Injectable()
export class EInvoicesService implements IEInvoicesService {
  constructor(
    @inject(HttpService)
    private readonly http: IHttpService,
  ) {}

  async list(params?: EInvoiceListParams): Promise<EInvoiceListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) {
      searchParams.set('status', params.status);
    }
    if (params?.docType) {
      searchParams.set('docType', params.docType);
    }
    if (params?.search) {
      searchParams.set('search', params.search);
    }
    if (params?.tenantId) {
      searchParams.set('tenantId', params.tenantId);
    }
    if (params?.from) {
      searchParams.set('from', params.from);
    }
    if (params?.to) {
      searchParams.set('to', params.to);
    }
    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip));
    }
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take));
    }
    if (params?.orderBy) {
      searchParams.set('orderBy', params.orderBy);
    }
    if (params?.orderDir) {
      searchParams.set('orderDir', params.orderDir);
    }

    const query = searchParams.toString();
    return this.http.get<EInvoiceListResponse>(`admin/e-invoices${query ? `?${query}` : ''}`);
  }

  async getOne(id: string): Promise<EInvoiceListItem> {
    return this.http.get<EInvoiceListItem>(`admin/e-invoices/${id}`);
  }

  async generate(stripeInvoiceId: string, force = false): Promise<EInvoiceListItem> {
    const query = force ? '?force=true' : '';
    return this.http.post<EInvoiceListItem>(`admin/e-invoices/${stripeInvoiceId}/generate${query}`);
  }

  async generateBatch(stripeInvoiceIds: string[]): Promise<GenerateBatchResult> {
    return this.http.post<GenerateBatchResult>('admin/e-invoices/generate-batch', {
      stripeInvoiceIds,
    });
  }

  async exportZip(stripeInvoiceIds: string[]): Promise<Blob> {
    const response = await this.http.postBlob('admin/e-invoices/export-zip', {
      stripeInvoiceIds,
    });
    return response.blob();
  }

  async downloadXml(id: string): Promise<Blob> {
    const response = await this.http.getBlob(`admin/e-invoices/${id}/download`);
    return response.blob();
  }
}
